import { handleExtensionMessageWithResponse } from '@root/app/_utils/extension';
import type { DefaultScribeUser } from '@root/v2/types';
import { parse, serialize } from 'cookie'; // eslint-disable-line no-restricted-imports
import type { GetServerSidePropsContext, NextPageContext } from 'next';
import getConfig from 'next/config';
import type { ResponseCookie } from 'next/dist/compiled/@edge-runtime/cookies';
import type { ReadonlyRequestCookies } from 'next/dist/server/web/spec-extension/adapters/request-cookies';
import type { NextRequest, NextResponse } from 'next/server';
import Cookies, { type CookieChangeListener, type CookieSetOptions } from 'universal-cookie'; // eslint-disable-line no-restricted-imports
import { v4 as uuidv4 } from 'uuid';

import { getRuntimeEnv } from '@/_utils/environment';

type UnifiedCookieStore = Cookies | (Omit<ReadonlyRequestCookies, 'get'> & { get: (name: string) => string | undefined | null });

/**
 * DO NOT IMPORT `cookies`!
 *
 * Its usage needs to be contained within this file so that we can make sure all of our
 * cookie logic is consistent across the whole app and the listeners work as intended.
 *
 * It's only exported for testing purposes.
 */
export const cookies = new Cookies();

const cookiesPreventOverwrite = (name: string, value: string, options?: CookieSetOptions): void => {
  const currentValue = cookies.get(name);

  // Only compare values since we can't get current options
  if (currentValue === value) {
    return;
  }

  cookies.set(name, value, options);
};

const globalChangeListener: { ref: null | CookieChangeListener } = { ref: null };

const COOKIE_KEYS = { LAST_SIGNED_IN_USER: 'lastSignedInUser', CUSTOM_URLS: 'customUrls' };
const FLAGSMITH_COOKIE_NAME = 'flagsmithUniqueID';

export const USER_UUID_COOKIE_NAME = 'user_uuid';

const tenYearsFromNow = () => {
  const today = new Date();

  return new Date(today.getFullYear() + 10, today.getMonth(), today.getDate());
};

const aMonthFromNow = () => {
  const date = new Date();

  return new Date(date.getFullYear(), date.getMonth() + 1, date.getDate());
};

function getCookieSetOptions(options: CookieSetOptions = {}): CookieSetOptions {
  const { isSecureEnv: secure, cookieDomain } = getRuntimeEnv();

  return {
    path: '/',
    domain: cookieDomain,
    expires: aMonthFromNow(),
    secure,
    sameSite: secure ? 'none' : undefined,
    ...options
  };
}

const checkAndSetSingleTenantUrlCookies = () => {
  const { isInSingleTenantProductionEnv, frontendUrl, backendUrl, suggestionsBackendUrl } = getRuntimeEnv();

  if (!isInSingleTenantProductionEnv) return;

  cookies.set(
    COOKIE_KEYS.CUSTOM_URLS,
    { frontendUrl, backendUrl, suggestionsBackendUrl },
    getCookieSetOptions({ expires: tenYearsFromNow() })
  );
};

export const setTokenCookie = ({
  access,
  refresh,
  preventOverwrite
}: {
  access: string;
  refresh?: string;
  preventOverwrite?: boolean;
}): void => {
  const options = getCookieSetOptions();

  if (preventOverwrite) {
    cookiesPreventOverwrite('data', access, options);

    if (refresh) {
      cookiesPreventOverwrite('refresh_data', refresh, options);
      checkAndSetSingleTenantUrlCookies();
    }
  } else {
    cookies.set('data', access, options);

    if (refresh) {
      cookies.set('refresh_data', refresh, options);
      checkAndSetSingleTenantUrlCookies();
    }
  }
};

// Used for Cookie Debugger and testing
export const setExpiredTokenCookie = ({ access, refresh }: { access: string; refresh?: string }): void => {
  const today = new Date();
  const options = getCookieSetOptions({ expires: new Date(today.getFullYear() - 10, today.getMonth(), today.getDate()) });

  cookies.set('data', access, options);

  if (refresh) {
    cookies.set('refresh_data', refresh, options);
    checkAndSetSingleTenantUrlCookies();
  }
};

const parseCustomUrls = (customUrls: string | undefined): { frontendUrl: string; backendUrl: string } | undefined => {
  if (typeof customUrls === 'object' && customUrls !== null) return customUrls;
  try {
    return customUrls ? JSON.parse(decodeURIComponent(customUrls)) : undefined;
  } catch {
    return undefined;
  }
};

export const getJwtPayload = (token: unknown): { exp?: number; user_id?: number } | null => {
  if (typeof token !== 'string') return null;

  try {
    const parts = token.split('.');

    if (parts.length !== 3) return null;
    return JSON.parse(atob(parts[1]));
  } catch {
    return null;
  }
};

const isValidJwt = (token: unknown): boolean => {
  const payload = getJwtPayload(token);

  if (payload === null) return false;

  try {
    return payload.exp > Math.floor(Date.now() / 1000);
  } catch {
    return false;
  }
};

export const getJwtUserId = (token: unknown): number | null => {
  const userId = getJwtPayload(token)?.user_id;

  return typeof userId === 'number' ? userId : null;
};

export const getTokenCookie = ({
  context,
  request
}: { context?: NextPageContext | GetServerSidePropsContext | null; request?: NextRequest } = {}): {
  token: string | undefined;
  refresh: string | undefined;
  customUrls: { frontendUrl: string; backendUrl: string } | undefined;
} => {
  if (request) {
    const access = request.cookies.get('data')?.value;
    const refresh = request.cookies.get('refresh_data')?.value;
    const customUrls = request.cookies.get(COOKIE_KEYS.CUSTOM_URLS)?.value;

    return {
      token: isValidJwt(access) ? access : undefined,
      refresh: isValidJwt(refresh) ? refresh : undefined,
      customUrls: parseCustomUrls(customUrls)
    };
  }

  if (context?.req?.headers.cookie) {
    const cookieString = typeof context?.req?.headers?.cookie === 'string' ? context?.req?.headers?.cookie : '';
    const { data: access, refresh_data: refresh, [COOKIE_KEYS.CUSTOM_URLS]: customUrls } = parse(cookieString);

    return {
      token: isValidJwt(access) ? access : undefined,
      refresh: isValidJwt(refresh) ? refresh : undefined,
      customUrls: parseCustomUrls(customUrls)
    };
  }

  const access = cookies.get('data');
  const refresh = cookies.get('refresh_data');

  return {
    token: isValidJwt(access) ? access : undefined,
    refresh: isValidJwt(refresh) ? refresh : undefined,
    customUrls: cookies.get(COOKIE_KEYS.CUSTOM_URLS)
  };
};

const clientOrServerCookies = async (): Promise<UnifiedCookieStore> => {
  if (typeof document !== 'undefined') return new Cookies();
  // only import in server
  const serverCookies = (await import('next/headers')).cookies();

  return { ...serverCookies, get: (name: string) => serverCookies.get(name)?.value };
};

export const shouldSkipMiddleware = (request: NextRequest) => {
  return request.cookies.get('skipMiddleware')?.value === 'true';
};

export const shouldSidepanelUseNewViewerPage = (request: NextRequest) => {
  return request.cookies.get('sidepanelNewViewer')?.value === 'true';
};

// Temporary while we hook up guide me for sidepanel. This allows us to test while we are coding.
export const allowSideKickNewViewer = async (): Promise<boolean> => {
  const cookieStore = await clientOrServerCookies();

  return !!cookieStore.get('allowSideKickNewViewer');
};

export const getAsyncTokenCookie = async (): Promise<{
  token?: string;
  refresh?: string;
  singleTenantUrls?: { frontendUrl: string; backendUrl: string };
}> => {
  const cookieStore = await clientOrServerCookies();
  const token = cookieStore.get('data');
  const refresh = cookieStore.get('refresh_data');
  const singleTenantUrls = cookieStore.get(COOKIE_KEYS.CUSTOM_URLS) || undefined;

  return {
    token: isValidJwt(token) ? token : undefined,
    refresh: isValidJwt(refresh) ? refresh : undefined,
    singleTenantUrls: parseCustomUrls(singleTenantUrls)
  };
};

const deleteSingleTenantUrlCookies = () => {
  const { cookieDomain } = getRuntimeEnv();

  cookies.remove(COOKIE_KEYS.CUSTOM_URLS, { path: '/', domain: cookieDomain });
};

export const removeTokenCookie = () => {
  const { cookieDomain } = getRuntimeEnv();

  cookies.remove('data', { path: '/', domain: cookieDomain });
  cookies.remove('refresh_data', { path: '/', domain: cookieDomain });
  cookies.remove('data');
  cookies.remove('refresh_data');
  deleteSingleTenantUrlCookies();
};

export const registerOnChangeCallbacks = (
  tokenChangedCallback: (value: string) => void,
  refreshChangedCallback: (value: string) => void
): void => {
  const { cookieDomain } = getRuntimeEnv();

  if (globalChangeListener.ref) {
    cookies.removeChangeListener(globalChangeListener.ref);
  }
  globalChangeListener.ref = ({ name, value, options }) => {
    if (options?.path === '/' && options?.domain === cookieDomain) {
      if (name === 'data') {
        // Perform the single-tenant cookie manipulation
        if (value) {
          checkAndSetSingleTenantUrlCookies();
        } else {
          deleteSingleTenantUrlCookies();
        }
        tokenChangedCallback(value);
      } else if (name === 'refresh_data') {
        if (value) {
          checkAndSetSingleTenantUrlCookies();
        } else {
          deleteSingleTenantUrlCookies();
        }
        refreshChangedCallback(value);
      }
    }
  };

  // Doesn't work across tabs/windows
  cookies.addChangeListener(globalChangeListener.ref);
};

const getSetCookieHeader = (context: NextPageContext | GetServerSidePropsContext): string[] => {
  if (!context.res) return [];
  const headerSetCookie = context.res.getHeader('Set-Cookie');

  if (Array.isArray(headerSetCookie)) return headerSetCookie;
  return !headerSetCookie ? [] : [String(headerSetCookie)];
};

export const getOrCreateFlagsmithAnonymousUser = async (): Promise<string> => {
  const cookieStore = await clientOrServerCookies();
  let flagsmithId = cookieStore.get(FLAGSMITH_COOKIE_NAME);

  if (!flagsmithId) {
    flagsmithId = crypto.randomUUID();
    cookieStore.set(FLAGSMITH_COOKIE_NAME, flagsmithId, getCookieSetOptions({ expires: tenYearsFromNow() }));
  }

  return flagsmithId;
};

export const setUserIdIfDoesNotExist = async ({ response }: { response: NextResponse }): Promise<void> => {
  const userUUID = (await import('next/headers')).cookies().get(USER_UUID_COOKIE_NAME)?.value;

  if (userUUID) return;

  const newUserUUID = crypto.randomUUID();

  response.cookies.set(USER_UUID_COOKIE_NAME, newUserUUID);
};

export const checkFlagsmithAnonymousUser = (): string => {
  let flagsmithUserId = cookies.get(FLAGSMITH_COOKIE_NAME);

  if (!flagsmithUserId) {
    flagsmithUserId = uuidv4();
    cookies.set(FLAGSMITH_COOKIE_NAME, flagsmithUserId, getCookieSetOptions({ expires: tenYearsFromNow() }));
  }

  return flagsmithUserId;
};

export const clearFlagsmithAnonymousUserCookie = () => {
  cookies.remove(FLAGSMITH_COOKIE_NAME);
};

export function addTokensToTheCookies({
  access,
  refresh,
  context
}: {
  access: string;
  refresh?: string;
  context?: NextPageContext | GetServerSidePropsContext;
}): void {
  if (!context?.res) {
    // Client-side
    setTokenCookie({ access, refresh });
    return;
  }

  const options = getCookieSetOptions();

  const cookieStrings = [...getSetCookieHeader(context), serialize('data', access, options)];

  if (refresh) {
    cookieStrings.push(serialize('refresh_data', refresh, options));
  }

  context.res.setHeader('Set-Cookie', cookieStrings);
}

export const generateTokenCookies = ({ access, refresh }: { access: string; refresh?: string }) => {
  const responseCookies: ResponseCookie[] = [{ name: 'data', value: access, expires: aMonthFromNow() }];

  if (refresh) {
    responseCookies.push({ name: 'refresh_data', value: refresh, expires: aMonthFromNow() });

    const { backendUrl, frontendUrl, isInSingleTenantProductionEnv } = getRuntimeEnv();

    if (isInSingleTenantProductionEnv) {
      const value = JSON.stringify({ frontendUrl, backendUrl });
      const expires = tenYearsFromNow();

      responseCookies.push({ name: COOKIE_KEYS.CUSTOM_URLS, value, expires });
    }
  }

  return responseCookies;
};

export const getMetaCookies = async () => {
  const cookieStore = await clientOrServerCookies();

  const fbc = cookieStore.get('_fbc');
  const fbp = cookieStore.get('_fbp');

  return {
    fbc: fbc || null,
    fbp: fbp || null
  };
};

export const getLastSignedInUser = async (): Promise<Pick<DefaultScribeUser, 'id' | 'email'> | null> => {
  const cookieStore = await clientOrServerCookies();
  const lastSignedInUser = cookieStore.get('lastSignedInUser');

  try {
    if (typeof lastSignedInUser === 'string') {
      return JSON.parse(decodeURIComponent(lastSignedInUser));
    }

    return lastSignedInUser || null;
  } catch {
    return null;
  }
};

export const setLastSignedInUser = (user: Pick<DefaultScribeUser, 'id' | 'email'>) => {
  cookies.set(
    'lastSignedInUser',
    {
      id: user.id,
      email: user.email
    },
    {
      domain: getConfig().publicRuntimeConfig.cookieDomain,
      expires: tenYearsFromNow(),
      path: '/',
      secure: true
    }
  );
};

export const getNextResponseCookies = (response: NextResponse) => response.cookies.getAll();

export const isLoggedInSSR = (context: NextPageContext | GetServerSidePropsContext) => {
  if (typeof context?.req?.headers?.cookie !== 'string') return false;

  const parsedCookies = parse(context?.req?.headers.cookie ?? '');

  const token = parsedCookies.data;
  const refresh = parsedCookies.refresh_data;

  return isValidJwt(token) && isValidJwt(refresh);
};

export const getLoggedInStatus = () => {
  const { refresh } = getTokenCookie();

  return {
    isLoggedIn: !!refresh
  };
};

export const getCookiesFromContextOrStoreOrExtension = async (
  context: NextPageContext | GetServerSidePropsContext | null = null
): Promise<{
  token: string | undefined;
  refresh: string | undefined;
  customUrls: { frontendUrl: string; backendUrl: string } | undefined;
}> => {
  const { token, refresh } = getTokenCookie({ context });

  const isIframe = typeof window !== 'undefined' && window.self !== window.top;
  const shouldAskExtension = isIframe && !token && !refresh;

  if (!shouldAskExtension) return { token, refresh, customUrls: undefined };

  const fallback: { token: string | undefined; refresh: string | undefined; customUrls: string | undefined } = {
    token: undefined,
    refresh: undefined,
    customUrls: undefined
  };

  let output;

  try {
    output = await handleExtensionMessageWithResponse({ messageType: 'getAuthToken' }, fallback, fallback);
  } catch {
    output = fallback;
  }
  if (output === null) {
    // @ts-expect-error - TODO: Handle this edge case
    return fallback;
  }

  if (output.token) {
    output.token = isValidJwt(output.token) ? output.token : undefined;
  }
  if (output.refresh) {
    output.refresh = isValidJwt(output.refresh) ? output.refresh : undefined;
  }

  if (typeof output.customUrls === 'string') {
    try {
      output.customUrls = JSON.parse(decodeURIComponent(output.customUrls));
    } catch {
      output.customUrls = undefined;
    }
  } else if (typeof output.customUrls !== 'object' || output.customUrls === null) {
    output.customUrls = undefined;
  }
  return output;
};
